import { useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import { batchCreateKontragents, createKontragentIn1C } from "@/api/mappings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { extractErrorMessage } from "@/lib/api-errors";
import type { KontragentCreateRequest, KontragentToCreate } from "@/types/api";
import { useConnection } from "@/auth/ConnectionContext";

interface Props {
  kontragents: KontragentToCreate[];
}

function toRequest(k: KontragentToCreate): KontragentCreateRequest {
  // In our context ALL ФизическоеЛицо kontragents come from XML <СвИП>
  // (individual entrepreneurs on marketplaces). This includes ИП, ГКФХ
  // and other ЕГРИП-registered forms — all are ИндивидуальныйПредприниматель
  // in 1С regardless of the specific ОПФ.
  const isIp = k.legal_type === "ФизическоеЛицо";
  return {
    inn: k.inn,
    kpp: k.kpp,
    name: k.name,
    full_name: k.full_name,
    legal_type: k.legal_type,
    is_individual_entrepreneur: isIp,
    opf_short: k.opf_short,
    ogrn: k.ogrn,
    registration_date: k.registration_date,
  };
}

export function MissingKontragentsPanel({
  kontragents,
}: Props): React.ReactElement | null {
  const queryClient = useQueryClient();
  const { activeConnectionId: connId } = useConnection();
  const [creating, setCreating] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  // Optimistic: INNs already created this session — removed from the
  // visible list immediately so a quick "Создать всех" click can't
  // duplicate them before verify-readiness re-runs.
  const [createdInns, setCreatedInns] = useState<Set<string>>(new Set());

  const pending = kontragents.filter((k) => !createdInns.has(k.inn));

  if (pending.length === 0) return null;

  const handleCreateOne = async (k: KontragentToCreate) => {
    setCreating(k.inn);
    try {
      const result = await createKontragentIn1C(toRequest(k));
      if (result.ok) {
        toast.success(`Контрагент ${k.name} создан в 1С`);
        setCreatedInns((prev) => new Set([...prev, k.inn]));
        queryClient.invalidateQueries({ queryKey: ["invoices", connId] });
        queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
      } else {
        toast.error(`Ошибка: ${result.error ?? "не удалось создать"}`);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Не удалось создать контрагента"));
    } finally {
      setCreating(null);
    }
  };

  const handleBatchCreate = async () => {
    setConfirmAll(false);
    setBatchRunning(true);
    try {
      const results = await batchCreateKontragents(
        pending.map(toRequest),
      );
      const ok = results.filter((r) => r.ok).length;
      const failed = results.length - ok;
      const newInns = results.filter((r) => r.ok).map((r) => r.inn);
      setCreatedInns((prev) => new Set([...prev, ...newInns]));
      if (failed === 0) {
        toast.success(`Создано ${ok} контрагентов`);
      } else {
        toast.warning(`Создано ${ok} из ${results.length}. Ошибок: ${failed}`);
      }
      queryClient.invalidateQueries({ queryKey: ["invoices", connId] });
      queryClient.invalidateQueries({ queryKey: ["invoices-verify", connId] });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Не удалось создать контрагентов"));
    } finally {
      setBatchRunning(false);
    }
  };

  return (
    <>
      <Card className="border-red-500/40 bg-red-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-red-800">
            <UserPlus className="size-4" />
            Не найдены в 1С ({pending.length}{" "}
            {pending.length === 1
              ? "контрагент"
              : pending.length < 5
                ? "контрагента"
                : "контрагентов"}
            )
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Эти контрагенты отсутствуют в справочнике 1С. Создайте их одной
            кнопкой — файлы автоматически станут готовыми к загрузке.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {pending.map((k) => (
            <div
              key={k.inn}
              className="flex items-center justify-between gap-3 rounded-md border bg-background p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {k.opf_short && (
                    <span className="mr-1 rounded bg-muted px-1.5 py-0.5 text-xs font-semibold">
                      {k.opf_short}
                    </span>
                  )}
                  {k.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  ИНН {k.inn}
                  {k.kpp && <> / КПП {k.kpp}</>}
                  {k.ogrn && <> · ОГРН {k.ogrn}</>}
                  {" · "}
                  {k.status === "LIQUIDATED" ? (
                    <span className="font-medium text-destructive">
                      ликвидирован
                    </span>
                  ) : k.status === "ACTIVE" ? (
                    <span className="text-green-700">действующий</span>
                  ) : k.status ? (
                    <span className="text-amber-700">{k.status}</span>
                  ) : k.legal_type === "ФизическоеЛицо" ? (
                    "ИП"
                  ) : (
                    "ЮЛ"
                  )}
                  {" · "}
                  <span className="font-medium text-foreground">
                    {k.file_count}{" "}
                    {k.file_count === 1
                      ? "файл"
                      : k.file_count < 5
                        ? "файла"
                        : "файлов"}
                  </span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={creating === k.inn || batchRunning}
                onClick={() => handleCreateOne(k)}
              >
                {creating === k.inn ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  "Создать в 1С"
                )}
              </Button>
            </div>
          ))}

          {pending.length > 1 && (
            <Button
              className="mt-2 w-full"
              disabled={batchRunning}
              onClick={() => setConfirmAll(true)}
            >
              {batchRunning ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Создаю…
                </>
              ) : (
                <>
                  <UserPlus className="size-4" />
                  Создать всех {pending.length} контрагентов в 1С
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmAll} onOpenChange={(v) => !v && setConfirmAll(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Создать контрагентов в 1С</DialogTitle>
            <DialogDescription>
              Следующие записи будут добавлены в справочник «Контрагенты» вашей
              базы 1С:
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {pending.map((k) => (
              <div key={k.inn} className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="font-medium">{k.name}</div>
                <div className="text-xs text-muted-foreground">
                  ИНН {k.inn}
                  {k.kpp && <> / КПП {k.kpp}</>}
                  {" · "}
                  {k.legal_type === "ФизическоеЛицо" ? "Физ. лицо" : "Юр. лицо"}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAll(false)}>
              Отмена
            </Button>
            <Button onClick={handleBatchCreate}>
              <UserPlus className="size-4" />
              Создать {pending.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
